import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {{ id: number, product: number, color: string, size: string | null, available: boolean, price: number }} BundleVariant
 */

/**
 * Canvas bundle: pick a top, a bottom and a charm color. Shows a live counter,
 * the running total and the savings, then adds the three variants to the cart
 * as separate lines so each one's inventory is decremented.
 * The price reduction itself comes from the automatic discount in Shopify admin.
 */
class CanvasBundleComponent extends HTMLElement {
  /** @type {Array<{ element: HTMLElement, variants: BundleVariant[], discount: number, hasSizes: boolean, defaultImage: string }>} */
  #steps = [];

  connectedCallback() {
    this.#steps = [...this.querySelectorAll('.canvas-bundle__step')].map((element) => {
      const step = /** @type {HTMLElement} */ (element);
      const image = step.querySelector('.canvas-bundle__image');

      return {
        element: step,
        variants: JSON.parse(step.querySelector('[data-variants]')?.textContent || '[]'),
        discount: Number(step.dataset.discount) || 0,
        hasSizes: step.querySelector('input[name$="-size"]') !== null,
        defaultImage: image instanceof HTMLImageElement ? image.src : '',
      };
    });

    this.addEventListener('change', this.#update);
    this.querySelector('[data-add]')?.addEventListener('click', this.#addToCart);
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#update);
    this.querySelector('[data-add]')?.removeEventListener('click', this.#addToCart);
  }

  /**
   * @param {HTMLElement} element
   * @param {string} suffix
   */
  #checked(element, suffix) {
    const input = element.querySelector(`input[name$="-${suffix}"]:checked`);
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * Keeps the size buttons in sync with the chosen color and returns the chosen variant.
   * @param {(typeof this.#steps)[number]} step
   * @returns {BundleVariant | null}
   */
  #syncStep(step) {
    const colorInput = this.#checked(step.element, 'color');
    const [productId, color] = colorInput ? colorInput.value.split('::') : [];
    const colorVariants = step.variants.filter(
      (variant) => String(variant.product) === productId && variant.color === color
    );

    const colorName = step.element.querySelector('[data-color-name]');
    if (colorName) colorName.textContent = colorInput ? `: ${colorInput.dataset.color}` : '';

    const image = step.element.querySelector('.canvas-bundle__image');
    if (image instanceof HTMLImageElement) {
      const nextSrc = colorInput?.dataset.image || step.defaultImage;
      if (nextSrc && image.src !== nextSrc) {
        image.removeAttribute('srcset');
        image.src = nextSrc;
      }
    }

    if (!colorInput) return null;
    if (!step.hasSizes) return colorVariants.find((variant) => variant.available) ?? null;

    for (const input of step.element.querySelectorAll('input[name$="-size"]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const match = colorVariants.find((variant) => variant.size === input.value);
      input.disabled = !match?.available;
      if (input.disabled) input.checked = false;
    }

    const sizeInput = this.#checked(step.element, 'size');
    if (!sizeInput) return null;

    return colorVariants.find((variant) => variant.size === sizeInput.value && variant.available) ?? null;
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || '');
  }

  /**
   * @param {string} template
   * @param {Record<string, string | number>} values
   */
  #fill(template = '', values) {
    return template.replace(/{{\s*(\w+)\s*}}/g, (_, key) => String(values[key] ?? ''));
  }

  /** @returns {Array<BundleVariant | null>} */
  #selection() {
    return this.#steps.map((step) => this.#syncStep(step));
  }

  #update = () => {
    const selection = this.#selection();
    const total = this.#steps.length;
    const done = selection.filter(Boolean).length;
    const isComplete = done === total;
    const missingKeys = ['textMissingTop', 'textMissingBottom', 'textMissingCharm'];

    let fullPrice = 0;
    let savings = 0;
    let potentialSavings = 0;

    this.#steps.forEach((step, index) => {
      const variant = selection[index];
      step.element.classList.toggle('is-complete', Boolean(variant));

      const price = variant?.price ?? step.variants[0]?.price ?? 0;
      const stepSavings = Math.round((price * step.discount) / 100);
      potentialSavings += stepSavings;

      if (variant) {
        fullPrice += variant.price;
        savings += stepSavings;
      }

      const stepPrice = step.element.querySelector('[data-step-price]');
      if (stepPrice && variant) {
        stepPrice.innerHTML = step.discount
          ? `<s>${this.#money(price)}</s> ${this.#money(price - stepSavings)}`
          : this.#money(price);
      }
    });

    if (!isComplete) savings = 0;

    const counter = this.querySelector('[data-counter]');
    if (counter) counter.textContent = this.#fill(this.dataset.textCounter, { count: done, total });

    const bar = this.querySelector('[data-bar]');
    if (bar instanceof HTMLElement) bar.style.width = `${(done / total) * 100}%`;

    const missing = this.querySelector('[data-missing]');
    if (missing) {
      const items = selection
        .map((variant, index) => (variant ? null : this.dataset[missingKeys[index] ?? '']))
        .filter(Boolean)
        .join(', ');
      missing.textContent = isComplete ? this.dataset.textComplete || '' : this.#fill(this.dataset.textMissing, { items });
    }

    const totalElement = this.querySelector('[data-total]');
    if (totalElement) totalElement.textContent = this.#money(fullPrice - savings);

    const compare = this.querySelector('[data-compare-total]');
    if (compare) compare.textContent = savings ? this.#money(fullPrice) : '';

    const savingsElement = this.querySelector('[data-savings]');
    if (savingsElement) {
      savingsElement.textContent = savings
        ? this.#fill(this.dataset.textSave, { amount: this.#money(savings) })
        : done > 0
          ? this.#fill(this.dataset.textAlmost, { amount: this.#money(potentialSavings) })
          : '';
    }

    this.#setMessage('');
  };

  /**
   * @param {string} text
   * @param {boolean} [isError]
   */
  #setMessage(text, isError = false) {
    const message = this.querySelector('[data-message]');
    if (!message) return;
    message.textContent = text;
    message.classList.toggle('is-error', isError);
  }

  #sectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`);
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  #addToCart = async () => {
    const selection = this.#selection();

    if (selection.some((variant) => !variant)) {
      this.#setMessage(this.dataset.textIncomplete || '', true);
      return;
    }

    const variants = /** @type {BundleVariant[]} */ (selection);
    const button = this.querySelector('[data-add]');
    if (button instanceof HTMLButtonElement) button.disabled = true;
    this.#setMessage(this.dataset.textAdding || '');

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: variants.map((variant) => ({ merchandiseId: String(variant.id), quantity: 1 })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: variants.map((variant) => ({
            id: variant.id,
            quantity: 1,
            properties: { _bundle: this.dataset.bundleName || 'Canvas' },
          })),
          sections: this.#sectionIds(),
        }),
      }).then((result) => result.json());

      const ajaxCart = await this.#fetchCart();
      const didError = Boolean(response.status);

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError,
          items: ajaxCart.items,
          source: 'canvas-bundle-component',
          itemCount: variants.length,
          sections: didError ? undefined : response.sections,
        },
      });

      if (didError) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        this.#setMessage(response.description || response.message || this.dataset.textError || '', true);
      } else {
        this.#setMessage(this.dataset.textAdded || '');
      }
    } catch (error) {
      console.error(error);
      deferredEventPromise.reject(error);
      this.#setMessage(this.dataset.textError || '', true);
    } finally {
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  };
}

if (!customElements.get('canvas-bundle-component')) {
  customElements.define('canvas-bundle-component', CanvasBundleComponent);
}
